import { useContext } from "react";
import { Bookmark } from "lucide-react";
import { HighlightScope, HighlightText, MarkTermsCtx, markTerms } from "@/components/highlights/HighlightScope";
import { useCommentaryBookmarks } from "./useCommentaryBookmarks";

export const sectionKeys = (s) => ["s", ...s.paragraphs.map((_, i) => String(i))];

/* One commentary section (title, quoted scripture, paragraphs) as a highlightable scope. */
export function CommentarySection({ s, slug, name, actions, highlights, active = false, onJump, compact = false }) {
  const keys = sectionKeys(s);
  const bm = useCommentaryBookmarks();
  const terms = useContext(MarkTermsCtx);
  const saved = bm.isSaved(slug, s.id);
  const mention = () => ({ kind: "commentary", id: `${slug}/${s.id}`, label: `${name} · ${s.reference}`, href: s.href });
  return (
    <HighlightScope
      scope={{ source: "commentary", commentary: slug, section_id: s.id, book: s.book, chapter: s.chapter }}
      keys={keys}
      highlights={highlights}
      actions={actions}
      mention={mention}
      className={`scroll-mt-24 rounded-2xl border p-5 transition-colors sm:p-6 ${active ? "border-gold/60 bg-gold/[0.06]" : "border-border bg-card"}`}
      testId={`commentary-section-${s.id}`}
    >
      <div id={s.id} className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <button type="button" onClick={() => onJump?.(s.verse_start)} className="no-reftag text-left font-mono text-[11px] uppercase tracking-wider text-gold hover:underline" data-testid={`commentary-section-ref-${s.id}`}>
          {s.reference}
        </button>
        <span className="flex items-center gap-2">
          {s.date && <span className="font-mono text-[10px] text-muted-foreground">{s.date}</span>}
          <button type="button" onClick={() => bm.toggle(slug, s.id)} disabled={bm.pending} aria-pressed={saved} title={saved ? "Saved to your Dashboard — click to remove" : "Save this section to your Dashboard"}
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${saved ? "text-gold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`} data-testid={`commentary-bookmark-${s.id}`}>
            <Bookmark className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
          </button>
        </span>
      </div>
      {s.title && <h3 className={`font-serif font-semibold text-foreground ${compact ? "text-lg" : "text-xl sm:text-2xl"}`}>{markTerms(s.title, terms, "title")}</h3>}
      {s.scripture && (
        <p className={`no-reftag mt-3 border-l-2 border-gold/50 pl-3 font-serif italic leading-relaxed text-foreground/80 ${compact ? "text-[15px]" : "text-base"}`}>
          <HighlightText k="s" text={s.scripture} />
        </p>
      )}
      <div className={`mt-4 space-y-3 font-serif leading-relaxed text-foreground/90 ${compact ? "text-[15px]" : "text-[17px]"}`}>
        {s.paragraphs.map((p, i) => <p key={`${s.id}-${i}`}><HighlightText k={String(i)} text={p} /></p>)}
      </div>
    </HighlightScope>
  );
}
